import { PAGE_ROUTES } from '~/constants/pages';
import {
  YTTV_BASIC,
  WIFI_SECURITY_PLUS,
  WIFI_SECURITY,
  WHOLE_HOME_WIFI_PLUS,
  NETFLIX_BUNDLE,
  MY_PREMIUM_TECH_PRO,
} from '~/constants/entityIds';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { InputHTMLAttributes, useContext, useEffect, useState } from 'react';
import { FuelContext } from '~/providers/FuelProvider';
import { Address } from '~/types/address';
import { Legal } from '~/types/legal';
import slugify from '~/helpers/slugify';
import Cookies from 'js-cookie';
import {
  CarouselProps,
  ContactGetResponse,
  CreditBypassResponse,
  ModalKeys,
  Modals,
  Product,
  SelectedProduct,
  SelectedProductRequest,
  SelectedProductsState,
  SelectProductsResponse,
  SweetenerKeys,
} from '~/types/products';
import useFetch from '../useFetch';
import {
  selectProduct,
  setLandingPageProduct,
  setLandingPageProductAvailable,
  setPhoneSelection,
  setSelectedNetflix,
  setSelectedSweetener,
} from '~/redux/actions/products';
import { ReactSelectOption } from '~/types/common';
import addMoney from '~/helpers/addMoney';
import clientSide from '~/helpers/clientSide';
import { Feature } from '~/types/products';
import { isWifiSecureTrialActive, isVrcSelected, isWholeHomeWifiTrialActive } from '~/helpers/products';
import { setCredit, setOneTimePassword } from '~/redux/actions/contact-credit';
import { setNewRelicCustomAttribute, setNewRelicPageAction, setTrackingCustomAttributes } from '~/helpers/newrelic';
import { Contact } from '~/types/contact-credit';
import {
  removeToken,
  setC2F,
  setDiscountOptions,
  setInstallationOptions,
  setPageLoading,
  setTracking,
} from '~/redux/actions/common';
import { DEFAULT_API_VERSION } from '~/constants/config/common';
import useUserActivity from '~/hooks/useUserActivity';
import { resetAutoPay } from '~/redux/actions/billing';
import checkVrcExperiencedEnabled from '~/helpers/checkVrcExperiencedEnabled';
import { createDiscountOptions } from '~/helpers/createDiscountOptions';
import { PLAN_SPEEDS_IDS, NetflixConfigKey } from '~/constants/products';

interface ReturnType {
  installAddress: Address | Record<string, unknown>;
  activeProducts: Product[];
  secureProducts: Product[];
  videoProducts: Product[];
  bannerText: string;
  isCopper: boolean;
  bundleCost: number;
  contact?: Contact;
  is200?: boolean;
  is500?: boolean;
  is1Gig?: boolean;
  is2Gig?: boolean;
  is5Gig?: boolean;
  is7Gig?: boolean;
  productFilters: {
    options: ReactSelectOption[];
    setOptions: (options: ReactSelectOption[]) => void;
    sweetener: ReactSelectOption;
    setSweetener: (selected: ReactSelectOption) => void;
    voiceAvailable: boolean;
    handleAddHomePhone: (event: InputHTMLAttributes<HTMLInputElement>) => void;
    handleAddProductState: (product: Product, option: Product) => void;
    addPhoneState: boolean;
  };
  handleSelectProduct: (product: Product, redirect?: boolean, selectWifiSecureFreeTrial?: boolean) => void;
  handleBundleSelection: (vasProducts: unknown, index: boolean) => void;
  selectedState: SelectedProductsState;
  setSelectedState: (products: SelectedProductsState) => void;
  singleProduct: boolean;
  twoProducts?: boolean;
  carousel: CarouselProps;
  bundleFeatures: Feature[];
  activeModal: Modals;
  setActiveModal: (key?: Modals) => void;
  legalMap: Legal['legalMap'];
  setEeroModal: (product: Product) => void;
  setVasModal: (product: Product) => void;
  setCompatibilityModal: (product: Product) => void;
  setUpsellModal: () => void;
  setBroadbandConsumerLabelModal: (product: Product) => void;
  setYTVModal: () => void;
  isCopper2Fiber: boolean;
  callInNumber: string;
  clickToRevealVas: boolean;
  setClickToRevealVas: (value: boolean) => void;
  setNFLSTModal: () => void;
  clickToToggleNetflixBundle: boolean;
  setClickToToggleNetflixBundle: (value: boolean) => void;
  showPlanCardBanner: boolean;
}
const { VRC, WIFI_SECURITY_FREE_TRIAL, WHOLE_HOME_WIFI_FREE_TRIAL } = SweetenerKeys;

const usePlanPackagePage = (): ReturnType => {
  const {
    address: { install: installAddress = {} } = {},
    contact,
    products,
    products: { available = {}, selected, broadbandType = '', planPageSelections, landingPageProduct } = {},
    tracking,
    legal,
    c2f,
    preamp,
    callInNumber,
    monarch,
    landingPage,
    token,
    singleForm,
    customOffers,
  } = useSelector((store: ReduxState) => store);
  const { legalMap } = legal || {};
  const { quoteNumber = '' } = tracking || {};
  const dispatch = useDispatch();
  const [selectedState, setSelectedState] = useState<SelectedProductsState>({
    internet: selected?.internet,
    voice: selected?.voice,
    secure: selected?.secure,
    vas: selected?.vas,
    video: selected?.video,
  });
  const { router, trackEvent } = useContext(FuelContext);
  const isCopper = broadbandType?.toLowerCase().includes('copper');
  const { selectedAddonBundle, selectedNetflix: selectedNetflixFromBroadband } = planPageSelections || {};
  const [options, setOptions] = useState(() => {
    const arr = [];
    if (available.broadband?.some((product) => product?.sweeteners?.vrc)) {
      arr.push({ value: 'visa', label: 'Claim Rewards' });
    }
    arr.push({ value: 'skip', label: 'Skip Rewards' });
    return arr;
  });
  const [bundlesState, setBundlesState] = useState([]);
  const [bundleIndex, setBundleIndex] = useState(null);
  setTrackingCustomAttributes(tracking);

  if (available?.broadband?.length > 0) {
    Cookies.set('highestAvailableSpeed', slugify(available.broadband[available.broadband.length - 1].name));
  }

  Cookies.set('fiberEligibleAddress', !isCopper);

  const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
  const [sweetener, setSweetener] = useState<ReactSelectOption>(isCopper ? options[1] : options[0]);
  const {
    data: selectProductsResponse,
    error: selectProductsError,
    fetch: selectProducts,
  } = useFetch<SelectProductsResponse>(`${apiVersion}/products`, { method: 'POST' });
  const creditBypass = useFetch<CreditBypassResponse>(`${apiVersion}/credit-check/bypass`, { method: 'post' }, true);
  const contactGet = useFetch<ContactGetResponse>(`${apiVersion}/contact/get`, { method: 'get' }, true);
  const { pathname } = router;
  const currentPage = pathname?.toLowerCase();

  const bundleCost = (() => {
    if (selectedState?.voice) {
      const discount = selectedState?.voice?.sweeteners?.discount?.amount || 0;
      const price = selectedState?.voice?.price?.amount || 0;
      return addMoney(price, discount);
    }
    return 0;
  })();
  const bundleFeatures = selectedState?.voice ? selectedState.voice.features : [];

  // Promotion banner
  const bannerText = (() => {
    if (isCopper) return 'Save $10 on internet each month with auto pay and get a Wi-Fi router included.';
    return '<b>Limited Time Offer:</b><br/>Order now for exclusive bonuses like an included router, $120 off a year of YouTube TV and more!';
  })();
  // Modal states
  const [activeModal, setActiveModal] = useState<Modals>();
  const [addedToCartTrackingObject, setAddedToCartTrackingObject] = useState({ detail: {} });

  // Carousel
  const [[productIndex, direction], setPage] = useState([0, 0]);
  const paginate = (newDirection: number): void => {
    setPage([productIndex + newDirection, newDirection]);
  };
  const isCopper2Fiber = c2f?.enabled;
  const { isUserTimedout } = useUserActivity();

  // Handle VRC Experiences
  const { vrcExperienceEnabled, vrcExperienceModalEnabled, vrcExperienceDelayPlanCardBannerEnabled } =
    checkVrcExperiencedEnabled();
  const [showPlanCardBanner, setShowPlanCardBanner] = useState(() => {
    if (vrcExperienceDelayPlanCardBannerEnabled && vrcExperienceModalEnabled) return false;
    return true;
  });

  const [wasVrcModalShown, setWasVRCModalShown] = useState(false);

  useEffect(() => {
    if (isUserTimedout) {
      setActiveModal(ModalKeys.VRC_OFFER);
      if (!wasVrcModalShown) {
        setWasVRCModalShown(true);
      }
      if (!showPlanCardBanner) {
        setShowPlanCardBanner(true);
      }
    }
  }, [isUserTimedout]);

  useEffect(() => {
    if (clientSide && window.fuse) {
      window.fuse('meta', { isCopperServiceable: String(isCopper), isFiberServiceable: String(!isCopper) });
    }
  }, []);

  useEffect(() => {
    if (landingPage?.persisted) {
      dispatch(removeToken());
      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'CART STATE',
          location: 'product_selection_page',
          position: 'PERSISTED FROM MLP',
          text: 'SAME ADDRESS',
        },
      });
    }
  }, [landingPage?.persisted, token]);

  useEffect(() => {
    if (customOffers?.wifiSecureTrialEnabled === true && customOffers?.wifiSecureTrialVersion === 'v1') {
      Cookies.set('addressOfferType', 'eeroFree');
    }
  }, [customOffers?.wifiSecureTrialEnabled, customOffers?.wifiSecureTrialVersion]);

  // Handle netflix toggle
  const [clickToToggleNetflixBundle, setClickToToggleNetflixBundle] = useState(false);
  useEffect(() => {
    // Set netflixProduct cookie if LP sets netflixProduct sessionStorage
    const netflixProductSessionStorage = clientSide ? window?.sessionStorage.getItem('netflixProduct') : null;
    const netflixProductResults = JSON.parse(netflixProductSessionStorage);
    Cookies.set('netflixProduct', netflixProductResults);

    delete selectedState?.video?.[NETFLIX_BUNDLE];

    if (clickToToggleNetflixBundle) {
      // Deselect uncompatabile add-ons if netflix bundle is toggled on
      delete selectedState?.vas?.WIFI_SECURITY;
      delete selectedState?.vas?.WHOLE_HOME_WIFI;
      delete selectedState?.vas?.WIFI_SECURITY_PLUS;
      delete selectedState?.secure?.[WHOLE_HOME_WIFI_PLUS];

      // Deselect 7GIG product and add-ons if netflix bundle is toggled on and 7gig was selected
      // 7GIG is not compatible with netflix bundle
      if (selectedState?.internet?.productId === PLAN_SPEEDS_IDS['7000']) {
        delete selectedState?.secure?.[MY_PREMIUM_TECH_PRO];
        delete selectedState?.internet;
      }
    }
    dispatch(setSelectedNetflix(clickToToggleNetflixBundle));
  }, [clickToToggleNetflixBundle]);

  // Handle product selection on landing pages
  // Only use landing page product on initial cart entrance, otherwise use selected product from redux
  const [clickToRevealVas, setClickToRevealVas] = useState(false);
  useEffect(() => {
    if (clientSide && window?.sessionStorage?.getItem('selectedProductId') && !selectedState?.internet?.productId) {
      const id = window?.sessionStorage?.getItem('selectedProductId');
      dispatch(setLandingPageProduct(id)); // add product ID to redux store

      // Check if user is serviceabile for the product they selected on the landing page
      const product = products?.available?.broadband?.find((item) => item?.productId === id);

      if (!product) return;

      dispatch(setLandingPageProductAvailable(true)); // used to determine headline copy
      setClickToRevealVas(true); // force users to click 'Continue' before revealing add-ons
      handleSelectProduct(product, false); // pre-select product
    }
  }, []);

  const singleFormEnabled = singleForm?.enabled === true || false;
  // Fetch callbacks.
  useEffect(() => {
    if (selectProductsResponse?.success) {
      // Reset auto pay vars in case user goes backwards in the flow
      dispatch(resetAutoPay());

      dispatch(selectProduct(selectedState, isCopper));
      dispatch(setCredit(selectProductsResponse));
      dispatch(setInstallationOptions('availableInstallOptions', selectProductsResponse?.installationOptions));
      dispatch(
        setOneTimePassword({
          success: selectProductsResponse?.contactVerifyComplete,
          codesGenerated: 0,
          codeVerifyAttempts: 0,
          verificationError: false,
          verificationRequiredFromQuote: selectProductsResponse?.contactVerifyRequired,
          attemptsPerCode: {
            1: 0,
            2: 0,
          },
        })
      );

      // Only apply discount vrc experience is enabled
      // And the banner is visible -> after the delay is complete
      if (vrcExperienceEnabled) {
        const discountOptions = createDiscountOptions({ selectedState, wasVrcModalShown });
        dispatch(setDiscountOptions(discountOptions));
      }

      if (isCopper2Fiber) {
        setNewRelicCustomAttribute([{ name: 'isCopper2Fiber', value: 'true' }]);
        const creditBypassFetch = () => {
          return creditBypass.fetch({
            body: JSON.stringify({
              email: contact?.email || '',
            }),
          });
        };

        (async () => {
          const data = await Promise.all(
            [creditBypassFetch, contactGet.fetch].map(async (fetch) => {
              const response = await fetch();
              return response;
            })
          );
          const [creditBypassData, contactGetData] = data as [CreditBypassResponse, ContactGetResponse];

          if (creditBypassData?.success && contactGetData?.success) {
            if (contactGetData?.accountUuid) dispatch(setTracking('accountUuid', contactGetData?.accountUuid));
            dispatch(setCredit(creditBypassData));
            dispatch(setC2F('hasAutoPay', contactGetData?.hasAutoPay));
            dispatch(setC2F('hasPaperlessBill', contactGetData?.hasPaperlessBill));
            dispatch(setC2F('hasEmailAddress', contact.email !== '' && contact.email !== null));
            if (!singleFormEnabled) {
              router.push(PAGE_ROUTES.ORDER_FIBER);
            } else {
              router.push(PAGE_ROUTES.CHECKOUT);
            }
            addToCartEvent(selectProductsResponse);
            //if single form IS enabled.....push to /checkout
          } else {
            dispatch(setPageLoading(false));
          }
        })();
      } else {
        //if single flow is NOT enabled, push to contact credit
        if (!singleFormEnabled) {
          router.push(PAGE_ROUTES.CONTACT_CREDIT);
        } else {
          router.push(PAGE_ROUTES.CHECKOUT);
        }
        addToCartEvent(selectProductsResponse);
      }
    }
  }, [selectProductsResponse, selectProductsError]);

  const addToCartEvent = (selectProductsResponse: SelectProductsResponse): void => {
    const { billingSummary } = selectProductsResponse;
    const { lineItems } = billingSummary || {};
    const productLineItems = lineItems?.filter((item) => item.category === 'Product') || [];
    trackEvent({
      action: 'addedToCart',
      data: {
        ...addedToCartTrackingObject,
        detail: {
          ...addedToCartTrackingObject?.detail,
          internetId: selectedState?.internet?.productId,
          lineItems: productLineItems,
          ...(clickToToggleNetflixBundle !== undefined && { netflixBundle: clickToToggleNetflixBundle }),
        },
      },
    });
  };

  // Handlers
  const handleAddHomePhone = (): void => {
    let voice = undefined;

    if (available?.voice && !selectedState?.voice?.productId) {
      voice = available?.voice[0];
    }
    // Self Install: If phone is selected, only expert installation allowed
    Cookies.set('phoneSelected', Boolean(voice?.productId || ''));
    if (!preamp?.VASVersion) dispatch(setPhoneSelection(voice?.productId || ''));

    setSelectedState({
      ...selectedState,
      voice,
    });

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'VAS',
        location: 'product_selection_page',
        position: 'home_phone_add',
        text: `${available?.voice?.[0]?.name} ${selectedState?.voice?.productId ? 'Removed' : 'Added'}`,
      },
    });
  };

  const handleAddProductState = (product, option): void => {
    const eventData = {
      action: 'elementClicked',
      data: {
        elementType: 'VAS',
        location: 'VAS',
        position: `${slugify(product?.name, '_')}_add`,
        text: '',
      },
    };

    const productType = product?.type?.toLowerCase() ?? '';
    if (!productType) return;

    const stateKey = productType === 'vas' ? product.productKey : product.productId;

    const isActive = selectedState?.[productType]?.[stateKey];
    const deepStateCopy = structuredClone(selectedState);

    if (isActive && !option) {
      delete deepStateCopy?.[productType]?.[stateKey]?.configurations;
      delete deepStateCopy[productType]?.[stateKey];
    } else {
      const productTypeState = {
        ...deepStateCopy[productType],
        [stateKey]: product,
      };

      // Deselect wifi security if wifi security plus is toggled on
      if (productTypeState?.WIFI_SECURITY !== undefined && product?.productId === WIFI_SECURITY_PLUS) {
        delete productTypeState?.WIFI_SECURITY;
      }

      // Deselect wifi security plus and whole home wifi plus if netflix bundle is selected
      if (
        (deepStateCopy?.vas?.WIFI_SECURITY_PLUS !== undefined ||
          deepStateCopy?.secure?.[WHOLE_HOME_WIFI_PLUS] !== undefined) &&
        product?.productId === NETFLIX_BUNDLE
      ) {
        delete deepStateCopy?.vas?.WIFI_SECURITY_PLUS;
        delete deepStateCopy?.secure?.[WHOLE_HOME_WIFI_PLUS];
      }

      if (deepStateCopy?.vas?.WHOLE_HOME_WIFI !== undefined && product?.productId === WHOLE_HOME_WIFI_PLUS) {
        delete deepStateCopy?.vas?.WHOLE_HOME_WIFI;
      }
      if (deepStateCopy?.vas?.WIFI_SECURITY !== undefined && product?.productId === WHOLE_HOME_WIFI_PLUS) {
        delete deepStateCopy?.vas?.WIFI_SECURITY;
      }
      if (deepStateCopy?.vas?.WIFI_SECURITY_PLUS !== undefined && product?.productId === WHOLE_HOME_WIFI_PLUS) {
        delete deepStateCopy?.vas?.WIFI_SECURITY_PLUS;
      }

      if (option) {
        if (!productTypeState[stateKey].configurations) productTypeState[stateKey].configurations = [];

        // is this configuration already on the product?
        const isOptionSelected = Boolean(
          productTypeState[stateKey].configurations.find((opt) => {
            return opt.productId === option.productId;
          })
        );
        // remove the configuration if it's no longer selected
        if (isOptionSelected && (!option.selected || option.amount === 0)) {
          productTypeState[stateKey].configurations = productTypeState[stateKey].configurations.filter((opt) => {
            return opt.productId !== option.productId;
          });
          // update configuration to increment the amount
        } else if (isOptionSelected && option.selected && option.amount > 0) {
          productTypeState[stateKey].configurations.find((opt) => {
            return opt.productId === option.productId;
          }).amount = option?.amount;
        } else {
          // push configuration onto array
          productTypeState[stateKey].configurations.push({
            productId: option?.productId,
            productKey: option?.productKey,
            name: option?.name,
            active: true,
            amount: option.amount ?? 0,
          });
        }
      }
      deepStateCopy[productType] = productTypeState;
    }

    // determine if nested option is selected or deselected and set event text accordingly
    if (option) {
      eventData.data.text = `${option?.name} ${option?.selected ? 'Added' : 'Removed'}`;
    } else {
      eventData.data.text = `${product?.name} ${isActive ? 'Removed' : 'Added'}`;
    }

    setSelectedState(deepStateCopy);
    if (!clickToToggleNetflixBundle) trackEvent(eventData);
  };

  const handleBundleSelection = (vasProducts, index) => {
    const obj = {
      internet: selectedState?.internet,
      voice: selectedState?.voice,
      secure: selectedState?.secure,
      vas: selectedState?.vas,
      video: selectedState?.video,
    };

    setBundleIndex(index);
    setBundlesState([]);

    if (vasProducts.length === 0) {
      bundlesState.forEach((bundle) => {
        trackEvent({
          action: 'elementClicked',
          data: {
            elementType: 'VAS',
            location: 'product_selection_page',
            position: `${bundle.length === 4 ? 'premium_whole_home_wifi_bundle' : 'whole_home_wifi_bundle'}_removed`,
            text: `${bundle} Removed`,
          },
        });
      });
    }
    vasProducts.forEach((product) => {
      setBundlesState((prevBundlesState) => [...prevBundlesState, product.name]);
      trackEvent({
        action: 'elementClicked',
        data: {
          elementType: 'VAS',
          location: 'product_selection_page',
          position: `${vasProducts.length === 4 ? 'premium_whole_home_wifi_bundle' : 'whole_home_wifi_bundle'}_add`,
          text: `${product?.name} Added`,
        },
      });
      if (product?.type === 'Voice') {
        handleAddHomePhone();
      } else {
        const productType = product?.type?.toLowerCase() ?? '';
        if (!productType) return;

        const stateKey = productType === 'vas' ? product.productKey : product.productId;
        if (!obj[productType]) obj[productType] = {};

        obj[productType][stateKey] = product;
        // Keep manually selected addons selected when choosing a bundle option
        if (
          bundleIndex === index &&
          selectedAddonBundle?.productIds.some((item) => item === stateKey || item === product.productId)
        ) {
          delete obj[productType][stateKey];
        }
      }
    });
    if (Object.keys(obj).length) {
      setSelectedState(obj);
    }
  };

  const setEeroModal = (product) => {
    setSelectedState({
      ...selectedState,
      modal: product,
    });
    setActiveModal(ModalKeys.EERO);
  };
  const setVasModal = (product) => {
    setSelectedState({
      ...selectedState,
      modal: product,
    });
    setActiveModal(ModalKeys.VAS);
  };
  const setCompatibilityModal = (product) => {
    setSelectedState({
      ...selectedState,
      modal: product,
    });
    setActiveModal(ModalKeys.ADD_ON_COMPATIBILITY);
  };

  const setUpsellModal = () => {
    setActiveModal(ModalKeys.UPSELL);
  };

  const setBroadbandConsumerLabelModal = (product) => {
    setSelectedState({
      ...selectedState,
      modal: product,
    });
    setActiveModal(ModalKeys.BROADBAND_CONSUMER_LABEL);
  };

  const setYTVModal = () => {
    setActiveModal(ModalKeys.YTV);
  };

  const setNFLSTModal = () => {
    setActiveModal(ModalKeys.NFLST);
  };

  const handleSelectProduct = (product, redirect = true, selectWifiSecureFreeTrial = false, selectNetflixUpsellBundle = false) => {
    // Deep copy product to avoid mutating the product's sweeteners in `available` key
    const productCopy = JSON.parse(JSON.stringify(product));
    if (sweetener?.value === 'skip') {
      delete productCopy?.sweeteners?.[VRC];
    }

    // Is 7gig currently selected and a different broadband product is being selected?
    // YES: Remove 7gig included add-ons
    // NO: Leave add-ons selected
    const currentState = selectedState;

    if (
      currentState?.internet?.productId === PLAN_SPEEDS_IDS['7000'] &&
      product.productId !== PLAN_SPEEDS_IDS['7000']
    ) {
      delete currentState?.secure?.[MY_PREMIUM_TECH_PRO];
      delete currentState?.vas?.WIFI_SECURITY;
      delete currentState?.vas?.WHOLE_HOME_WIFI;
    }

    setSelectedState({
      ...currentState,
      internet: productCopy,
    });

    const postProduct: SelectedProduct = {
      productId: product?.productId,
      type: product?.type,
      name: product?.name,
      dataspeed: {
        downloadSpeedMbps: product?.dataspeed?.downloadInMbps || 0,
        uploadSpeedMbps: product?.dataspeed?.uploadInMbps || 0,
      },
      configurations: [],
    };

    if (product?.sweeteners?.[VRC]) {
      postProduct.configurations.push({
        productKey: product?.sweeteners?.[VRC]?.key,
        productId: product?.sweeteners?.[VRC]?.productId,
        active: isVrcSelected(products, product),
      });
    }

    if (product?.sweeteners?.[WIFI_SECURITY_FREE_TRIAL]) {
      postProduct.configurations.push({
        productKey: product?.sweeteners?.[WIFI_SECURITY_FREE_TRIAL]?.key,
        productId: product?.sweeteners?.[WIFI_SECURITY_FREE_TRIAL]?.productId,
        active: isWifiSecureTrialActive(customOffers, product, selectedState, selectWifiSecureFreeTrial),
      });
      //if the user adds eero via the wi-fi secure offer modal, add the eero secure product to the configurations array
      if (selectWifiSecureFreeTrial) {
        postProduct.configurations.push({
          productId: WIFI_SECURITY,
          productKey: 'WIFI_SECURITY',
          name: 'Wi-Fi Security',
          active: true,
        });
      }
    }

    if (product?.sweeteners?.[WHOLE_HOME_WIFI_FREE_TRIAL]) {
      postProduct.configurations.push({
        productKey: product?.sweeteners?.[WHOLE_HOME_WIFI_FREE_TRIAL]?.key,
        productId: product?.sweeteners?.[WHOLE_HOME_WIFI_FREE_TRIAL]?.productId,
        active: isWholeHomeWifiTrialActive(customOffers, product, selectedState) ?? false,
      });
    }

    if (selectedState?.vas) {
      const vasProducts = Object.values(selectedState?.vas);
      vasProducts.forEach((product) => {
        postProduct.configurations.push({
          productId: product.productId,
          productKey: product.productKey,
          name: product.name,
          active: true,
        });
      });
    }

    // Check if selected product is what they chose on the landing page
    // i.e. they did not change products once in cart
    const usedLPProduct = postProduct?.productId === landingPageProduct;

    const selectProductsRequest: SelectedProductRequest = {
      selectedProducts: [postProduct],
      isLandingPage: usedLPProduct,
    };

    if (selectedState?.voice?.productId) {
      selectProductsRequest.selectedProducts.push({
        productId: selectedState.voice.productId,
        type: selectedState.voice.type,
      });
    }

    if (selectedState?.secure) {
      const secureProducts = Object.values(selectedState?.secure);
      secureProducts.forEach((product) => {
        selectProductsRequest.selectedProducts.push({
          productId: product.productId,
          type: product.type,
          configurations: product?.configurations || [],
        });
      });
    }

    if (selectedState?.video) {
      const videoProducts = Object.values(selectedState?.video);
      videoProducts.forEach((product) => {
        if (product?.productId === NETFLIX_BUNDLE) {
          // If netflix is selected, set the config
          selectProductsRequest.selectedProducts.push({
            productId: product.productId,
            type: product.type,
            configurations: [
              {
                productId: product?.productId,
                productKey: product?.productKey,
                name: selectedNetflixFromBroadband
                  ? NetflixConfigKey.SELECTED_FROM_BROADBAND
                  : NetflixConfigKey.SELECTED_FROM_VAS,
                active: true,
              },
            ],
          });
        } else {
          selectProductsRequest.selectedProducts.push({
            productId: product.productId,
            type: product.type,
          });
        }
      });
    }

    // Add netflix bundle to cart if user selects it in Upsell modal
    if (selectNetflixUpsellBundle) {
      selectProductsRequest.selectedProducts.push({
        productId: NETFLIX_BUNDLE,
        type: 'Video',
      });
    }

    if (isCopper2Fiber) {
      setNewRelicCustomAttribute([{ name: 'isCopper2Fiber', value: 'true' }]);
    }

    // Preamp Decision: Add product selected
    Cookies.set('productSelected', product?.productId || '');

    const trackingObject = {
      detail: {
        products: selectProductsRequest.selectedProducts.map(({ productId }) => productId),
        newCart: true,
        upgraded: true,
        orderId: quoteNumber,
      },
    };

    setAddedToCartTrackingObject(trackingObject);
    trackEvent({ action: 'packageSelected', data: trackingObject });

    //   return;
    if (redirect) {
      const isGdt = currentPage.includes('/my-custom-quote');
      trackEvent({
        action: 'elementClicked',
        data: {
          elementType: 'BUTTON',
          action: 'SUBMIT',
          outboundUrl: currentPage.includes('checkout') ? '/checkout' : '/contact-credit',
          location: isGdt ? 'GDT RESULTS PAGE' : 'product_selection_page',
          text: isGdt ? 'CHECKOUT' : 'CONTINUE',
          position: isGdt ? 'sticky-checkout-footer' : 'vas',
        },
      });
    }

    trackEvent({
      action: 'productClicked',
      data: {
        name: product?.name,
        price: product?.price?.amount,
        quantity: 1,
        brand: 'Frontier',
        category: product?.type,
        variant: 'default',
        productId: product?.productId,
        action: 'add',
        location: usedLPProduct ? 'landing_page' : null,
      },
    });

    if (clickToToggleNetflixBundle) {
      trackEvent({
        action: 'elementClicked',
        data: {
          elementType: 'VAS',
          location: 'product_cards',
          position: 'netflix_and_pro_wi-fi_bundle_add',
          text: 'Netflix and Pro Wi-Fi Bundle Added',
        },
      });
    }
    setNewRelicPageAction('packageClicked');

    if (redirect) {
      selectProducts({
        body: JSON.stringify(selectProductsRequest),
      });
    }
  };

  // Prefetch the next page chunk.
  useEffect(() => {
    dispatch(setSelectedSweetener(isCopper ? options[1] : options[0]));
    router.prefetch(PAGE_ROUTES.CONTACT_CREDIT);
  }, []);

  // Filter out products without feature information
  const filteredProducts = available?.broadband?.filter((product) => {
    return product.features?.length > 0;
  });

  // return base plan only
  const filteredVideoProducts = available?.video?.filter((product) => {
    return product?.productId === YTTV_BASIC || product?.productId === NETFLIX_BUNDLE;
  });

  return {
    activeModal,
    activeProducts: filteredProducts || [],
    secureProducts: [...(available?.secure || []), ...(available?.voice || []), ...(available?.vas || [])],
    videoProducts: filteredVideoProducts || [],
    bannerText,
    bundleCost,
    bundleFeatures,
    contact,
    carousel: {
      direction,
      productIndex,
      paginate,
    },
    installAddress,
    isCopper,
    legalMap,
    productFilters: {
      addPhoneState: Boolean(selectedState?.voice?.productId),
      options,
      setOptions,
      sweetener,
      voiceAvailable: Boolean(available?.voice?.length),
      handleAddHomePhone,
      handleAddProductState,
      setSweetener,
    },
    selectedState,
    singleProduct: available?.broadband?.length === 1,
    twoProducts: available?.broadband?.length === 2,
    handleSelectProduct,
    handleBundleSelection,
    setEeroModal,
    setVasModal,
    setCompatibilityModal,
    setUpsellModal,
    setBroadbandConsumerLabelModal,
    setYTVModal,
    setSelectedState,
    setActiveModal,
    isCopper2Fiber,
    callInNumber,
    clickToRevealVas,
    setClickToRevealVas,
    setNFLSTModal,
    clickToToggleNetflixBundle,
    setClickToToggleNetflixBundle,
    showPlanCardBanner,
  };
};

export default usePlanPackagePage;
